/* import __COLOCATED_TEMPLATE__ from './sq-catalog-location-picker.hbs.js'; */
var _dec, _class, _descriptor, _descriptor2;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import ModalsSelectionModal from '@dashboard/shared-ui/components/modals/selection-modal';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { isNone, isPresent } from '@ember/utils';
import { itemSubunitsServiceActiveSubunits } from '@square/dashboard-utilities/utils/item-subunits';
import { subunitsLabel } from '@dashboard/junk-drawer/utils/subunits';

/**
### Overview

This component is a location selector field. It has a subunits label which reads:

  "All Locations"
  "X Locations"

and when clicked, it launches a modal which allows the user to select the desired
locations for the model.

### Basic Usage

In your template:
```hbs
<div class="form-row">
  {{#sq-form-field label="Location"}}
    {{sq-catalog-location-picker catalogModel=model.item
                                 canSetItemsGloballyEnabled=true}}
  {{/sq-form-field}}
</div>

### Overide title
<div class="form-row">
  {{#sq-form-field label="Location"}}
    {{sq-catalog-location-picker catalogModel=model.item
                                 title="Select Locations"
                                 canSetItemsGloballyEnabled=true}}
  {{/sq-form-field}}
</div>
```

### Parameters
@param {BaseCatalogModel} [catalogModel] BaseCatalogModel
@param {Boolean} [canSetItemsGloballyEnabled] Enables/Disables the "Available at all future locations" checkbox
@param {Function} [onLocationChange] Function called before the location of the catalog model is updated
@param {string[]} [disabledSubunitTokens] List of subunit tokens that are not selectable (DEFAULT: [])
*/
let SqCatalogLocationPicker = (_dec = computed('catalogModel.subunits.@each.nickname'), (_class = class SqCatalogLocationPicker extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "i18n", _descriptor, this);
    _initializerDefineProperty(this, "modal", _descriptor2, this);
    _defineProperty(this, "canSetItemsGloballyEnabled", true);
    _defineProperty(this, "title", null);
    _defineProperty(this, "catalogModel", null);
    _defineProperty(this, "onLocationChange", null);
    _defineProperty(this, "disabledSubunitTokens", []);
  }
  get subunitsLabel() {
    return subunitsLabel(this.catalogModel?.subunits.mapBy('nickname'), itemSubunitsServiceActiveSubunits().length);
  }
  editLocations() {
    const subunits = itemSubunitsServiceActiveSubunits();
    const catalogModel = this.catalogModel;
    const disabledSubunitTokens = this.disabledSubunitTokens;
    const options = subunits.sortBy('nickname').map(subunit => ({
      checked: catalogModel.enabledForUnit(subunit.get('token')),
      value: subunit,
      label: subunit.get('nickname'),
      disabled: disabledSubunitTokens.includes(subunit.get('token'))
    }));
    if (this.canSetItemsGloballyEnabled) {
      options.push({
        checked: catalogModel.get('globallyEnabled'),
        label: this.i18n.t('items.itemLibrary.edit.subunits.futureLocations'),
        value: null
      });
    }
    this.modal.open(ModalsSelectionModal, {
      title: this.title || this.i18n.t('subunit.edit'),
      selectAllLabel: this.i18n.t('subunit.subunits'),
      options
    }).then(savedOptions => {
      if (isPresent(this.onLocationChange) && isPresent(savedOptions)) {
        return this.onLocationChange(savedOptions);
      } else {
        return savedOptions;
      }
    }).then(savedOptions => {
      if (isNone(savedOptions)) {
        return;
      }
      const catalogModel = this.catalogModel;
      const globallyEnabledOption = this.canSetItemsGloballyEnabled ? savedOptions.pop() : null;
      if (globallyEnabledOption) {
        const {
          didChange,
          checked: globallyEnabled
        } = globallyEnabledOption;
        if (didChange) {
          catalogModel.set('globallyEnabled', globallyEnabled);
          // updateGloballyEnabledForRelationships may not be defined on a Catalog model
          catalogModel.updateGloballyEnabledForRelationships?.(globallyEnabled);
        }
      }
      for (const {
        checked,
        value: subunit
      } of savedOptions.filterBy('didChange')) {
        const token = subunit.get('token');
        if (checked) {
          catalogModel.addMerchantSubunit(subunit);
          if (isPresent(catalogModel.addAffectedRelatedObjects)) {
            catalogModel.addAffectedRelatedObjects(token);
          }
        } else {
          if (isPresent(catalogModel.removeAffectedRelatedObjects)) {
            catalogModel.removeAffectedRelatedObjects(token);
          }
          catalogModel.removeMerchantSubunit(token);
        }
      }
    });
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "modal", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "subunitsLabel", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "subunitsLabel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "editLocations", [action], Object.getOwnPropertyDescriptor(_class.prototype, "editLocations"), _class.prototype)), _class));
export { SqCatalogLocationPicker as default };